import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { errMsg } from "@/lib/api";
import { FIRM } from "@/lib/firm";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState("");

  const sendReset = async () => {
    if (!email.trim()) return setError("Enter your email first");
    setLoading(true); setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSent(data.message);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      nav("/");
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between bg-[#0B0D10] text-white p-12 grid-noise relative">
        <div className="absolute inset-0 bg-[#0B0D10]/95" />
        <div className="relative">
          <div className="flex items-center gap-3">
            <img src={FIRM.logo} alt="" className="w-12 h-12 object-contain bg-white p-1" data-testid="firm-logo" />
            <div>
              <div className="display text-2xl font-black">{FIRM.name}</div>
              <div className="label-xs text-white/40 mt-1">Authorized Personnel Only</div>
            </div>
          </div>
        </div>
        <div className="relative max-w-md">
          <h2 className="display text-4xl font-black leading-tight">SBC OS</h2>
          <p className="text-sm text-white/50 mt-5 leading-relaxed">
            Internal Operations Portal — the central operating system for managing
            every aspect of your business.
          </p>
        </div>
        <div className="relative" />
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form onSubmit={submit} className="w-full max-w-sm" data-testid="login-form">
          <div className="label-xs">Sign in</div>
          <h1 className="text-3xl font-black tracking-tight mt-2">Operations console</h1>
          <p className="text-sm text-muted-foreground mt-2">Use your team account to continue.</p>

          <div className="mt-8 space-y-4">
            <div>
              <Label className="label-xs">Email</Label>
              <Input
                data-testid="login-email-input"
                className="mt-1.5 rounded-[2px]"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="username"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label className="label-xs">Password</Label>
                <button type="button" data-testid="forgot-password-link"
                  onClick={() => { setForgot(!forgot); setSent(""); setError(""); }}
                  className="label-xs underline text-muted-foreground hover:text-[#0055FF] transition-colors duration-150">
                  Forgot password?
                </button>
              </div>
              <Input
                data-testid="login-password-input"
                type="password"
                className="mt-1.5 rounded-[2px]"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
          </div>

          {forgot && (
            <div className="mt-4 border border-border p-3" data-testid="forgot-panel">
              <p className="text-xs text-muted-foreground">
                We&rsquo;ll email a single-use link to <span className="font-medium">{email || "your address"}</span>. It expires in an hour.
              </p>
              <Button type="button" data-testid="send-reset-btn" variant="outline" disabled={loading}
                onClick={sendReset} className="rounded-[2px] mt-3 w-full">
                {loading ? "Sending…" : "Email me a reset link"}
              </Button>
              {sent && <div data-testid="reset-sent-msg" className="mt-3 text-xs text-[#1F7A45]">{sent}</div>}
            </div>
          )}

          {error && (
            <div data-testid="login-error" className="mt-4 text-sm text-[#E53E3E] border border-[#E53E3E]/30 bg-[#FEF2F2] px-3 py-2">
              {error}
            </div>
          )}

          <Button
            data-testid="login-submit-btn"
            disabled={loading}
            className="w-full mt-6 btn-primary h-11 font-semibold"
          >
            {loading ? "Signing in…" : "Sign in"}
          </Button>
        </form>
      </div>
    </div>
  );
}
